import { Component, OnInit } from '@angular/core';
import { SeoService } from '@app/core/seo/seo.service';
import { ensureLottiePlayer } from '@app/core/lottie/lottie-loader';
import { ConfigService } from 'esz-saas-lcp-ng-crud-ui';

@Component({
    standalone: false,
    selector: 'app-ws-homepage',
    templateUrl: './ws-homepage.component.html',
    styleUrls: ['./ws-homepage.component.scss']
})
export class WsHomepageComponent implements OnInit {

    /** `id` is the matching anchor on /services (the cards there carry the same ids). */
    services = [
        {
            icon: 'pi pi-th-large',
            id: 'hosted',
            title: 'BMS platform (SaaS)',
            description: 'Sixteen hosted business applications with single sign-on, fine-grained access control, isolated data per customer in the cloud, and a field-level audit trail on every change.'
        },
        {
            icon: 'pi pi-shopping-cart',
            id: 'commerce',
            title: 'Commerce & POS',
            description: 'Product catalog, e-commerce storefront, offline-capable point of sale, self-order kiosks, table QR ordering, kitchen display, digital signage, returns and payment terminals.'
        },
        {
            icon: 'pi pi-calendar',
            id: 'bookings',
            title: 'Bookings & operations',
            description: 'Reservations across 11 verticals for hotels, clinics, gyms, rentals, workspaces and residences — with waitlists, QR visitor passes, housekeeping and concierge workflows.'
        },
        {
            icon: 'pi pi-users',
            id: 'customers',
            title: 'Customers & growth',
            description: 'CRM leads and deals, loyalty points, tiers, customer wallets, promo codes, member referral campaigns, omni-channel messaging, customer portals and brand websites.'
        },
        {
            icon: 'pi pi-sitemap',
            id: 'back-office',
            title: 'BPMS, back office & automation',
            description: 'Business Process Management System (visual workflows, case management and decision tables), cloud document hub, dynamic forms, HRIS & payroll, and double-entry accounting.'
        },
        {
            icon: 'pi pi-code',
            id: 'custom',
            title: 'Custom development & consulting',
            description: 'Web and mobile applications, Integration Platform automations, managed cloud engineering, and the implementation work that gets you live.'
        }
    ];

    selectedSolutionIndex: number | null = null;
    
    /** The nine bubbles of the products orbit; index 0 (Items) is the default detail. `section` is the /services anchor the module belongs to. */
    webSolutions = [
        {
            icon: 'pi pi-tags',
            title: 'Items · Catalog',
            description: 'Products with variations, multi-pack barcode hierarchies, statutory unit pricing, shelf-edge labels, composite bundles, modifiers, allergen tags and an anonymous storefront API.',
            section: 'commerce'
        },
        {
            icon: 'pi pi-shopping-cart',
            title: 'Ecom & POS',
            description: 'Online storefront, offline-capable POS register with X/Z shift reports, self-order counter kiosks, table QR ordering with floor plans and course firing, B2B trade invoices, and GST/VAT tax engines.',
            section: 'commerce'
        },
        {
            icon: 'pi pi-calendar',
            title: 'Booking',
            description: 'Reservable assets and live availability across 11 verticals for hotels, clinics, gyms, rentals, coworking, events and residences, with rate plans, waitlists, QR visitor passes and housekeeping boards.',
            section: 'bookings'
        },
        {
            icon: 'pi pi-credit-card',
            title: 'Payments',
            description: 'Online payment links, physical card terminals, dynamic QR and UPI payments, split tenders, unmatched payment reconciliation and refund tracking shared by every module.',
            section: 'commerce'
        },
        {
            icon: 'pi pi-star',
            title: 'Loyalty',
            description: 'Points, tiers, digital gift cards, stored-value customer wallets, promo codes and member referral campaigns redeemable online and at the POS register across every outlet.',
            section: 'customers'
        },
        {
            icon: 'pi pi-comments',
            title: 'CRM & Communication',
            description: 'Leads, deals, segments and follow-ups, plus one message store for email, SMS, instant messaging, push and in-app notifications with workflow templates and automated bounce hygiene.',
            section: 'customers'
        },
        {
            icon: 'pi pi-file',
            title: 'Documents & Forms',
            description: 'A versioned cloud document hub with folder permissions and expiry alerts, plus a dynamic form builder with cross-module data lookups bound to website intake and workflow transitions.',
            section: 'back-office'
        },
        {
            icon: 'pi pi-sitemap',
            title: 'BPMS · Workflows',
            description: 'Business Process Management System with visual BPMN 2.0 workflows, CMMN 1.1 case management, DMN 1.3 decision tables, SLA timers and a unified work inbox, plus an Integration Platform for webhooks and automations.',
            section: 'back-office'
        },
        {
            icon: 'pi pi-briefcase',
            title: 'HR & Accounting',
            description: 'Employees, shifts, leave, attendance, payroll runs and external HR import; a double-entry general ledger with vouchers, bank reconciliation, tax returns and accounting XML/CSV sync.',
            section: 'back-office'
        }
    ];

    activateSolution(index: number) {
        this.selectedSolutionIndex = index;
    }

    constructor(public configService: ConfigService, private seo: SeoService) { }

    ngOnInit(): void {
        this.seo.set({
            title: 'ESZES Consultancy & Services | BMS business management software',
            description: 'BMS by ESZES is a 16-module business management suite: catalog and e-commerce, point of sale, bookings, payments, loyalty, CRM, documents, Business Process Management System (workflows, cases & decisions), HR and accounting, hosted per tenant in the cloud.',
            path: '/'
        });
        ensureLottiePlayer();
    }
}
